
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Toggle } from "@/components/ui/Toggle";
import {
  deleteDictionary,
  listDictionaries,
  putDictionary as saveDictionary,
} from "@/lib/api/endpoints";
import { slugify } from "@/lib/mapper/helpers";

type DictionaryEntryState = { from: string; to: string };

type DictionaryState = {
  id?: string;
  name: string;
  slug: string;
  entries: DictionaryEntryState[];
  caseInsensitive: boolean;
  trim: boolean;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function emptyDictionary(): DictionaryState {
  return {
    name: "",
    slug: "",
    entries: [{ from: "", to: "" }],
    caseInsensitive: true,
    trim: true,
  };
}

function cloneDictionary(dict: DictionaryState): DictionaryState {
  return {
    ...dict,
    entries: dict.entries.map((entry) => ({ ...entry })),
  };
}

function parseDictionary(value: unknown): DictionaryState | null {
  const raw = asRecord(value);
  if (!raw) return null;
  const name = typeof raw.name === "string" ? raw.name : "";
  const slug = typeof raw.slug === "string" ? raw.slug : "";
  if (!name || !slug) return null;
  const entries = Array.isArray(raw.entries)
    ? raw.entries
        .map((entry): DictionaryEntryState | null => {
          const rawEntry = asRecord(entry);
          if (!rawEntry) return null;
          return {
            from: typeof rawEntry.from === "string" ? rawEntry.from : "",
            to: typeof rawEntry.to === "string" ? rawEntry.to : "",
          };
        })
        .filter((entry): entry is DictionaryEntryState => entry !== null)
    : [];
  return {
    id: typeof raw.id === "string" ? raw.id : undefined,
    name,
    slug,
    entries,
    caseInsensitive:
      typeof raw.caseInsensitive === "boolean" ? raw.caseInsensitive : true,
    trim: typeof raw.trim === "boolean" ? raw.trim : true,
  };
}

function sortDictionaries(items: DictionaryState[]): DictionaryState[] {
  return [...items].sort(
    (a, b) => a.name.localeCompare(b.name) || a.slug.localeCompare(b.slug),
  );
}

async function requestDictionaries(): Promise<DictionaryState[]> {
  const json = await listDictionaries();
  if (!Array.isArray(json)) return [];
  return sortDictionaries(
    json
      .map(parseDictionary)
      .filter((dict): dict is DictionaryState => dict !== null),
  );
}

async function putDictionary(dict: DictionaryState): Promise<DictionaryState> {
  const saved = parseDictionary(
    await saveDictionary({
      name: dict.name,
      slug: dict.slug,
      entries: dict.entries.filter(
        (entry) => entry.from.length > 0 || entry.to.length > 0,
      ),
      caseInsensitive: dict.caseInsensitive,
      trim: dict.trim,
    }),
  );
  if (!saved) throw new Error("Invalid replacement list response");
  return saved;
}

function DictionarySaveBar({
  dirty,
  saving,
  saved,
  deleting,
  error,
  canDelete,
  onSave,
  onDelete,
}: {
  dirty: boolean;
  saving: boolean;
  saved: boolean;
  deleting: boolean;
  error: string | null;
  canDelete: boolean;
  onSave: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 dark:border-border pt-3">
      <button
        type="button"
        onClick={onDelete}
        disabled={!canDelete || saving || deleting}
        className="rounded-md border border-red-200 dark:border-red-900/60 px-2 py-1 text-[11px] text-red-700 dark:text-red-300 transition hover:bg-red-50 dark:hover:bg-red-950/40 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {deleting ? "Deleting…" : "Delete"}
      </button>

      <div className="flex items-center justify-end gap-3">
        {error ? (
          <span className="text-[12px] text-red-600 dark:text-red-400">{error}</span>
        ) : saved ? (
          <span className="font-mono text-[11px] text-emerald-600 dark:text-emerald-400">Saved ✓</span>
        ) : dirty ? (
          <span className="font-mono text-[11px] text-amber-600 dark:text-amber-400">Unsaved</span>
        ) : null}
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving || deleting}
          className="rounded-lg bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-zinc-800 dark:hover:bg-zinc-200 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

export function DictionariesCard() {
  const [items, setItems] = useState<DictionaryState[]>([]);
  const [draft, setDraft] = useState<DictionaryState | null>(null);
  const [base, setBase] = useState<DictionaryState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    requestDictionaries()
      .then((rows) => {
        if (!live) return;
        setItems(rows);
        const first = rows[0] ? cloneDictionary(rows[0]) : null;
        setDraft(first);
        setBase(first ? cloneDictionary(first) : null);
      })
      .catch((err) => {
        if (live) setError(err instanceof Error ? err.message : "Load failed");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const dirty = useMemo(
    () => !!draft && (!base || JSON.stringify(draft) !== JSON.stringify(base)),
    [draft, base],
  );

  const selectDictionary = (dict: DictionaryState) => {
    const next = cloneDictionary(dict);
    setDraft(next);
    setBase(cloneDictionary(next));
    setSaved(false);
    setError(null);
  };

  const startNew = () => {
    setDraft(emptyDictionary());
    setBase(null);
    setSaved(false);
    setError(null);
  };

  const patchDraft = (patch: Partial<DictionaryState>) => {
    setDraft((current) => (current ? { ...current, ...patch } : current));
    setSaved(false);
    setError(null);
  };

  const updateEntry = (index: number, patch: Partial<DictionaryEntryState>) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        entries: current.entries.map((entry, i) =>
          i === index ? { ...entry, ...patch } : entry,
        ),
      };
    });
    setSaved(false);
    setError(null);
  };

  const addEntry = () => {
    setDraft((current) =>
      current
        ? { ...current, entries: [...current.entries, { from: "", to: "" }] }
        : current,
    );
    setSaved(false);
    setError(null);
  };

  const removeEntry = (index: number) => {
    setDraft((current) =>
      current
        ? {
            ...current,
            entries: current.entries.filter((_, i) => i !== index),
          }
        : current,
    );
    setSaved(false);
    setError(null);
  };

  const onSave = useCallback(async () => {
    if (!draft) return;
    const name = draft.name.trim();
    const slug = draft.slug.trim();
    if (!name) {
      setError("Name is required");
      return;
    }
    if (!slug) {
      setError("Slug is required");
      return;
    }

    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const savedDict = await putDictionary({ ...draft, name, slug });
      const next = cloneDictionary(savedDict);
      setItems((current) => {
        const others = current.filter((item) => item.slug !== next.slug);
        return sortDictionaries([...others, cloneDictionary(next)]);
      });
      setDraft(next);
      setBase(cloneDictionary(next));
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const onDelete = useCallback(async () => {
    if (!draft) return;
    if (!base) {
      setDraft(null);
      setError(null);
      return;
    }
    if (!window.confirm(`Delete replacement list "${base.name}"? Replace Values settings using "${base.slug}" will miss until you choose another replacement list.`)) {
      return;
    }
    setDeleting(true);
    setError(null);
    setSaved(false);
    try {
      await deleteDictionary(
        base.id ? { id: base.id } : { slug: base.slug },
      );
      const remaining = items.filter((item) => item.slug !== base.slug);
      setItems(remaining);
      const next = remaining[0] ? cloneDictionary(remaining[0]) : null;
      setDraft(next);
      setBase(next ? cloneDictionary(next) : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeleting(false);
    }
  }, [base, draft, items]);

  return (
    <Card
      title="Replacement lists"
      subtitle="Reusable cleanup for colours, fabrics, and vendor spellings."
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr]">
        <div className="rounded-xl border border-zinc-200 dark:border-border">
          <div className="flex items-center justify-between gap-2 border-b border-zinc-100 dark:border-border px-3 py-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
              Saved
            </span>
            <button
              type="button"
              onClick={startNew}
              className="rounded-md border border-dashed border-zinc-300 dark:border-border px-2 py-1 text-[11px] text-zinc-600 dark:text-zinc-400 transition hover:border-zinc-900 dark:hover:border-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              + Add
            </button>
          </div>

          <div className="max-h-64 overflow-y-auto p-2">
            {loading ? (
              <p className="px-2 py-5 text-center text-[12px] text-zinc-400 dark:text-zinc-500">
                Loading replacement lists…
              </p>
            ) : items.length === 0 ? (
              <p className="px-2 py-5 text-center text-[12px] text-zinc-400 dark:text-zinc-500">
                No replacement lists yet.
              </p>
            ) : (
              <div className="space-y-1">
                {items.map((item) => {
                  const active = draft?.slug === item.slug && base?.slug === item.slug;
                  return (
                    <button
                      key={item.slug}
                      type="button"
                      onClick={() => selectDictionary(item)}
                      className={[
                        "w-full rounded-lg px-2 py-2 text-left transition",
                        active
                          ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-950"
                          : "hover:bg-zinc-50 dark:hover:bg-surface-2",
                      ].join(" ")}
                    >
                      <span className="block truncate text-[12px] font-semibold">
                        {item.name}
                      </span>
                      <span
                        className={[
                          "mt-0.5 block truncate font-mono text-[10.5px]",
                          active ? "text-white/70 dark:text-zinc-700" : "text-zinc-500 dark:text-zinc-400",
                        ].join(" ")}
                      >
                        {item.slug} · {item.entries.length} row
                        {item.entries.length === 1 ? "" : "s"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {draft ? (
          <div className="rounded-xl border border-zinc-200 dark:border-border p-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                  Name
                </span>
                <input
                  type="text"
                  value={draft.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    patchDraft({
                      name,
                      slug: base ? draft.slug : slugify(name),
                    });
                  }}
                  placeholder="Colors"
                  className="mt-1 w-full rounded-lg border border-zinc-200 dark:border-border bg-white dark:bg-surface px-3 py-2 text-[13px] text-zinc-900 dark:text-zinc-100 outline-none focus:border-zinc-900 dark:focus:border-zinc-400"
                />
              </label>

              <label className="block">
                <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                  Slug
                </span>
                <input
                  type="text"
                  value={draft.slug}
                  readOnly
                  placeholder="colors"
                  className="mt-1 w-full rounded-lg border border-zinc-200 dark:border-border bg-zinc-50 dark:bg-surface-2 px-3 py-2 font-mono text-[12px] text-zinc-700 dark:text-zinc-300 outline-none"
                />
              </label>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-4 rounded-lg border border-zinc-100 dark:border-border bg-zinc-50/50 dark:bg-surface/50 p-3">
              <label className="flex items-center gap-2">
                <Toggle
                  checked={draft.caseInsensitive}
                  onChange={(caseInsensitive) => patchDraft({ caseInsensitive })}
                  label="Case insensitive"
                />
                <span className="text-[12px] text-zinc-700 dark:text-zinc-300">
                  Case-insensitive match
                </span>
              </label>
              <label className="flex items-center gap-2">
                <Toggle
                  checked={draft.trim}
                  onChange={(trim) => patchDraft({ trim })}
                  label="Trim whitespace"
                />
                <span className="text-[12px] text-zinc-700 dark:text-zinc-300">
                  Trim whitespace
                </span>
              </label>
            </div>

            <div className="mt-4 overflow-hidden rounded-lg border border-zinc-200 dark:border-border">
              <table className="w-full text-[12px]">
                <thead className="bg-zinc-50 dark:bg-surface">
                  <tr className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                    <th className="px-3 py-2 text-left">From</th>
                    <th className="px-3 py-2 text-left">To</th>
                    <th className="w-8 px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {draft.entries.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-3 py-4 text-center text-[12px] text-zinc-400 dark:text-zinc-500"
                      >
                        No entries. Click &ldquo;+ Add row&rdquo; below.
                      </td>
                    </tr>
                  ) : (
                    draft.entries.map((entry, index) => (
                      <tr key={index} className="border-t border-zinc-100 dark:border-border">
                        <td className="px-3 py-1.5">
                          <input
                            type="text"
                            value={entry.from}
                            onChange={(e) =>
                              updateEntry(index, { from: e.target.value })
                            }
                            placeholder="rani pink"
                            className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-[12px] text-zinc-900 dark:text-zinc-100 outline-none hover:border-zinc-200 focus:border-zinc-900 dark:focus:border-zinc-400 focus:bg-white dark:focus:bg-surface-2"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            type="text"
                            value={entry.to}
                            onChange={(e) =>
                              updateEntry(index, { to: e.target.value })
                            }
                            placeholder="Pink"
                            className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-[12px] text-zinc-900 dark:text-zinc-100 outline-none hover:border-zinc-200 focus:border-zinc-900 dark:focus:border-zinc-400 focus:bg-white dark:focus:bg-surface-2"
                          />
                        </td>
                        <td className="px-1 py-1.5 text-center">
                          <button
                            type="button"
                            onClick={() => removeEntry(index)}
                            aria-label="Remove replacement row"
                            className="rounded p-1 text-zinc-400 dark:text-zinc-500 transition hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 dark:hover:text-red-300"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              onClick={addEntry}
              className="mt-2 rounded-md border border-dashed border-zinc-300 dark:border-border px-2 py-1 text-[11px] text-zinc-600 dark:text-zinc-400 transition hover:border-zinc-900 dark:hover:border-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              + Add row
            </button>

            <DictionarySaveBar
              dirty={dirty}
              saving={saving}
              saved={saved}
              deleting={deleting}
              error={error}
              canDelete={!!draft}
              onSave={onSave}
              onDelete={onDelete}
            />
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-zinc-300 dark:border-border p-6 text-center">
            <p className="text-[13px] font-medium text-zinc-700 dark:text-zinc-300">
              No replacement list selected
            </p>
            <p className="mt-1 text-[12px] text-zinc-500 dark:text-zinc-400">
              Create one to clean up colors, fabrics, and vendor spellings.
            </p>
            <button
              type="button"
              onClick={startNew}
              className="mt-3 rounded-lg bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-zinc-800 dark:hover:bg-zinc-200"
            >
              Add replacement list
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}
